import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row, Col, Card, Statistic, Table, Tag, Space, Typography, Spin, Alert,
  Button, Select, Modal, Form, Input, InputNumber, message, Descriptions, Empty
} from 'antd';
import {
  DollarOutlined, BulbOutlined, CheckCircleOutlined, SyncOutlined,
  ThunderboltOutlined, SwapOutlined, ClusterOutlined,
  SearchOutlined, EyeOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import { savingsService } from '../services/api';
import '../styles/Phase5.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const LEVER_TYPE_CONFIG = {
  payment_terms: { label: 'Payment Terms', color: 'blue', icon: <SwapOutlined /> },
  price_variance: { label: 'Price Variance', color: 'orange', icon: <ThunderboltOutlined /> },
  volume_consolidation: { label: 'Volume Consolidation', color: 'green', icon: <ClusterOutlined /> },
};

const PRIORITY_CONFIG = {
  critical: { color: 'red' },
  high: { color: 'volcano' },
  medium: { color: 'gold' },
  low: { color: 'default' },
};

const STATUS_CONFIG = {
  new: { color: 'blue', label: 'New' },
  reviewing: { color: 'processing', label: 'Reviewing' },
  planned: { color: 'purple', label: 'Planned' },
  implemented: { color: 'green', label: 'Implemented' },
  dismissed: { color: 'default', label: 'Dismissed' },
};

const SavingsOpportunities = () => {
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [opportunities, setOpportunities] = useState([]);

  // Filters
  const [leverType, setLeverType] = useState(null);
  const [status, setStatus] = useState(null);
  const [priority, setPriority] = useState(null);

  // Update modal
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [updateForm] = Form.useForm();

  const autoScanned = useRef(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsData, oppsData] = await Promise.all([
        savingsService.getStats(),
        savingsService.getOpportunities({
          lever_type: leverType,
          status,
          priority,
          limit: 100,
        }),
      ]);
      setStats(statsData);
      setOpportunities(oppsData);
      setError(null);
      return statsData;
    } catch (err) {
      setError('Failed to load savings data');
      message.error('Failed to load savings data');
      return null;
    } finally {
      setLoading(false);
    }
  }, [leverType, status, priority]);

  useEffect(() => {
    fetchData().then((statsData) => {
      // Auto-trigger first scan if one has never been run
      if (statsData && !statsData.last_scan_at && !autoScanned.current) {
        autoScanned.current = true;
        setScanning(true);
        savingsService.triggerScan({}).then((result) => {
          message.success(`Initial scan complete: ${result.opportunities_found} opportunities found`);
          fetchData();
        }).catch(() => {
          message.error('Auto-scan failed');
        }).finally(() => {
          setScanning(false);
        });
      }
    });
  }, [fetchData]);

  const handleScan = async () => {
    try {
      setScanning(true);
      const result = await savingsService.triggerScan({});
      message.success(`Scan complete: ${result.opportunities_found} opportunities found ($${result.total_estimated_savings.toLocaleString()} estimated savings)`);
      fetchData();
    } catch (err) {
      message.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleUpdateClick = (record) => {
    setSelectedOpp(record);
    updateForm.setFieldsValue({
      status: record.status,
      decision_notes: record.decision_notes || '',
      actual_savings: record.actual_savings,
    });
    setUpdateModalVisible(true);
  };

  const handleUpdateSubmit = async () => {
    try {
      const values = await updateForm.validateFields();
      await savingsService.updateOpportunity(selectedOpp.id, values);
      message.success('Opportunity updated');
      setUpdateModalVisible(false);
      fetchData();
    } catch (err) {
      message.error('Update failed');
    }
  };

  const columns = [
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: (val) => (
        <Tag color={PRIORITY_CONFIG[val]?.color || 'default'}>
          {val?.toUpperCase()}
        </Tag>
      ),
      sorter: (a, b) => {
        const order = { critical: 0, high: 1, medium: 2, low: 3 };
        return (order[a.priority] ?? 4) - (order[b.priority] ?? 4);
      },
    },
    {
      title: 'Type',
      dataIndex: 'lever_type',
      key: 'lever_type',
      width: 160,
      render: (val) => {
        const cfg = LEVER_TYPE_CONFIG[val];
        return cfg ? (
          <Tag icon={cfg.icon} color={cfg.color}>{cfg.label}</Tag>
        ) : val;
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 150,
      render: (val) => val || '-',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (val) => val || '-',
    },
    {
      title: 'Est. Savings',
      dataIndex: 'estimated_annual_savings',
      key: 'estimated_annual_savings',
      width: 130,
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.estimated_annual_savings || 0) - (b.estimated_annual_savings || 0),
      render: (val) => (
        <Text strong style={{ color: '#3f8600' }}>
          ${(val || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (val) => {
        const cfg = STATUS_CONFIG[val];
        return <Tag color={cfg?.color || 'default'}>{cfg?.label || val}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleUpdateClick(record)} />
          {record.status === 'new' && (
            <Button
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={async () => {
                await savingsService.updateOpportunity(record.id, { status: 'dismissed' });
                message.success('Dismissed');
                fetchData();
              }}
            />
          )}
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record) => {
    const meta = record.lever_metadata || {};
    return (
      <div style={{ padding: '8px 16px' }}>
        <Text type="secondary">{record.description}</Text>
        {record.lever_type === 'payment_terms' && meta.current_terms && (
          <Descriptions size="small" column={4} style={{ marginTop: 12 }}>
            <Descriptions.Item label="Current Terms">{meta.current_terms}</Descriptions.Item>
            <Descriptions.Item label="Suggested">{meta.suggested_terms}</Descriptions.Item>
            <Descriptions.Item label="Annual Spend">${(meta.annual_spend || 0).toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Days Gained">{meta.days_gained}</Descriptions.Item>
          </Descriptions>
        )}
        {record.lever_type === 'price_variance' && meta.variance_type === 'cross_vendor' && (
          <Descriptions size="small" column={3} style={{ marginTop: 12 }}>
            <Descriptions.Item label="High Cost">{meta.high_cost_vendor?.name} (${(meta.high_cost_vendor?.avg_monthly_spend || 0).toLocaleString()}/mo)</Descriptions.Item>
            <Descriptions.Item label="Low Cost">{meta.low_cost_vendor?.name} (${(meta.low_cost_vendor?.avg_monthly_spend || 0).toLocaleString()}/mo)</Descriptions.Item>
            <Descriptions.Item label="Price Gap">{meta.price_gap_pct}%</Descriptions.Item>
          </Descriptions>
        )}
        {record.lever_type === 'price_variance' && meta.variance_type === 'upward_trend' && (
          <Descriptions size="small" column={3} style={{ marginTop: 12 }}>
            <Descriptions.Item label="First Half Avg">${(meta.first_half_avg || 0).toLocaleString()}/mo</Descriptions.Item>
            <Descriptions.Item label="Second Half Avg">${(meta.second_half_avg || 0).toLocaleString()}/mo</Descriptions.Item>
            <Descriptions.Item label="Trend">{meta.trend_pct}% increase</Descriptions.Item>
          </Descriptions>
        )}
        {record.lever_type === 'volume_consolidation' && meta.vendors && (
          <Descriptions size="small" column={3} style={{ marginTop: 12 }}>
            <Descriptions.Item label="Vendors">{meta.vendor_count}</Descriptions.Item>
            <Descriptions.Item label="Combined Spend">${(meta.combined_annual_spend || 0).toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Anchor">{meta.anchor_vendor}</Descriptions.Item>
          </Descriptions>
        )}
      </div>
    );
  };

  if (loading && !stats) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <DollarOutlined style={{ marginRight: 8 }} />
          Savings Levers
        </Title>
        <Button
          type="primary"
          icon={<SearchOutlined />}
          loading={scanning}
          onClick={handleScan}
          size="large"
        >
          Run Scan
        </Button>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Estimated Savings"
              value={stats?.total_estimated_savings || 0}
              prefix="$"
              precision={0}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="New Opportunities"
              value={stats?.new_opportunities || 0}
              prefix={<BulbOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Implemented Savings"
              value={stats?.total_actual_savings || 0}
              prefix={<CheckCircleOutlined />}
              precision={0}
              valueStyle={{ color: '#52c41a' }}
              suffix={stats?.implemented ? ` (${stats.implemented})` : ''}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Last Scan"
              value={stats?.last_scan_at ? new Date(stats.last_scan_at).toLocaleDateString() : 'Never'}
              prefix={<SyncOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Lever Type Breakdown */}
      {stats?.by_lever_type && Object.keys(stats.by_lever_type).length > 0 && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          {Object.entries(stats.by_lever_type).map(([type, data]) => {
            const cfg = LEVER_TYPE_CONFIG[type];
            return (
              <Col xs={24} sm={8} key={type}>
                <Card size="small">
                  <Space>
                    {cfg?.icon}
                    <Text strong>{cfg?.label || type}</Text>
                  </Space>
                  <div style={{ marginTop: 8 }}>
                    <Text>{data.count} opportunities</Text>
                    <br />
                    <Text type="success">${(data.estimated || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} estimated</Text>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Lever Type"
            allowClear
            style={{ width: 180 }}
            value={leverType}
            onChange={setLeverType}
          >
            <Option value="payment_terms">Payment Terms</Option>
            <Option value="price_variance">Price Variance</Option>
            <Option value="volume_consolidation">Volume Consolidation</Option>
          </Select>
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 140 }}
            value={status}
            onChange={setStatus}
          >
            <Option value="new">New</Option>
            <Option value="reviewing">Reviewing</Option>
            <Option value="planned">Planned</Option>
            <Option value="implemented">Implemented</Option>
            <Option value="dismissed">Dismissed</Option>
          </Select>
          <Select
            placeholder="Priority"
            allowClear
            style={{ width: 130 }}
            value={priority}
            onChange={setPriority}
          >
            <Option value="critical">Critical</Option>
            <Option value="high">High</Option>
            <Option value="medium">Medium</Option>
            <Option value="low">Low</Option>
          </Select>
        </Space>
      </Card>

      {/* Opportunities Table */}
      <Card>
        {opportunities.length === 0 && !loading ? (
          <Empty
            description="No savings opportunities found. Click 'Run Scan' to analyze your spend data."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            dataSource={opportunities}
            columns={columns}
            rowKey="id"
            loading={loading}
            expandable={{ expandedRowRender }}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} opportunities` }}
          />
        )}
      </Card>

      {/* Update Modal */}
      <Modal
        title={selectedOpp?.title}
        open={updateModalVisible}
        onOk={handleUpdateSubmit}
        onCancel={() => setUpdateModalVisible(false)}
        okText="Save"
        width={600}
      >
        <Form form={updateForm} layout="vertical">
          <Form.Item name="status" label="Status">
            <Select>
              <Option value="new">New</Option>
              <Option value="reviewing">Reviewing</Option>
              <Option value="planned">Planned</Option>
              <Option value="implemented">Implemented</Option>
              <Option value="dismissed">Dismissed</Option>
            </Select>
          </Form.Item>
          <Form.Item name="decision_notes" label="Decision Notes">
            <TextArea rows={3} placeholder="Notes about this opportunity..." />
          </Form.Item>
          <Form.Item name="actual_savings" label="Actual Savings (if implemented)">
            <InputNumber
              style={{ width: '100%' }}
              prefix="$"
              min={0}
              placeholder="Enter actual savings achieved"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SavingsOpportunities;
